import ExcelJs from "exceljs";
import jsPDF from "jspdf";
import "jspdf-autotable";

const { Workbook } = ExcelJs;

export const exportPICostCsv = (columns, data, reportTitle) => {
  const summaryData = calculateSummary(data);
  const workbook = new Workbook();
  const worksheet = workbook.addWorksheet(reportTitle);
  worksheet.columns = [
    { header: "NDC", key: "ndc" },
    { header: "Description", key: "description" },
    { header: "Pack Size", key: "pack_size", type: "numeric" },
    { header: "Drug CD", key: "drug_cd" },
    { header: "Form", key: "form" },
    { header: "Gen Status", key: "gen_status" },
    {
      header: "Inventory Count (Units)",
      key: "inventory_count",
      type: "numeric",
    },
    {
      header: "Unit Cost",
      key: "unit_cost",
      type: "numeric",
      render: (row) =>
        row.unit_cost &&
        Number(row.unit_cost).toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
        }),
    },
    {
      header: "Ext Cost",
      key: "ext_cost",
      type: "numeric",
      render: (row) =>
        row.ext_cost &&
        Number(row.ext_cost).toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
        }),
    },
  ];

  data.forEach((product) => {
    product.ext_cost = formatCurrency(product.ext_cost);
    product.unit_cost = formatCurrency(product.unit_cost);
  });

  worksheet.addRows(data);
  worksheet.getRow(1).eachCell((cell) => (cell.font = { bold: true }));
  worksheet.addRow();
  worksheet.addRow();

  const startCell = "A" + (data.length + 4);
  const endCell = "C" + (data.length + 4);
  // Merged Cell for Summary table name
  worksheet.mergeCells(startCell + ":" + endCell);
  worksheet.getCell(startCell).value = "Summary";
  worksheet.getCell(startCell).style = {
    font: { bold: true },
    alignment: { horizontal: "center", vertical: "middle" },
  };

  worksheet.addRow(["Class", "Brand", "Generics"]);
  worksheet
    .getRow(data.length + 5)
    .eachCell((cell) => (cell.style = { font: { bold: true } }));
  worksheet.addRows(
    Object.entries(summaryData).map((entry) => {
      const [classname, data] = entry;
      return [
        classname,
        formatCurrency(data.brand),
        formatCurrency(data.generics),
      ];
    })
  );
  const totalCell = data.length + 4 + 7;

  // bolden group wise total row
  worksheet
    .getRow(totalCell - 1)
    .eachCell((cell) => (cell.style = { font: { bold: true } }));

  worksheet.getCell(`A${totalCell}`).value = "Total Value(Brand + Generics)"; // offset previous data
  worksheet.mergeCells(`B${totalCell}:C${totalCell}`);
  worksheet.getCell(`B${totalCell}`).value = formatCurrency(
    Number(summaryData.Total.brand) + Number(summaryData.Total.generics)
  );

  // bolden grand total row
  worksheet.getRow(totalCell).eachCell(
    (cell) =>
      (cell.style = {
        font: { bold: true },
        alignment: { horizontal: "center" },
      })
  );

  workbook.xlsx.writeBuffer().then((buffer) => {
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const downloadLink = document.createElement("a");
    downloadLink.href = URL.createObjectURL(blob);
    downloadLink.download = reportTitle + ".xlsx";

    document.body.appendChild(downloadLink);

    downloadLink.click();

    document.body.removeChild(downloadLink);
  });
};

export function calculateSummary(data) {
  const obj = { generics: 0, brand: 0 };
  const summary = {
    rx: { ...obj },
    cii: { ...obj },
    ciii_cv: { ...obj },
    otc: { ...obj },
    Total: { ...obj },
  };
  function addToSummary(item, category) {
    const amount = Number(item.ext_cost);
    if (item.gen_status === "G") {
      summary[category].generics += amount;
      summary.Total.generics += amount;
    } else if (item.gen_status === "B") {
      summary[category].brand += amount;
      summary.Total.brand += amount;
    }
  }
  for (const item of data) {
    switch (item.drug_cd) {
      case "2":
        addToSummary(item, "cii");
        break;
      case "3":
        addToSummary(item, "ciii_cv");
        break;
      case "4":
        addToSummary(item, "ciii_cv");
        break;
      case "5":
        addToSummary(item, "ciii_cv");
        break;
      case "6":
        addToSummary(item, "rx");
        break;
      case "7":
        addToSummary(item, "otc");
        break;
    }
  }
  return summary;
}

function formatCurrency(amount) {
  return Number(amount || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
}

export function exportPICostPdf(columns, data, reportTitle, store) {
  const summaryData = calculateSummary(data);

  const pdfDoc = new jsPDF("p", "mm", "a4", {
    margin: { top: 10, right: 0, bottom: 10, left: 0 },
  });
  pdfDoc.setFontSize(10);
  const x = 14;
  const y = 14;
  pdfDoc.text("85 Joan Dr", x, y);
  pdfDoc.text("Yonkers NY 10704", x, y + 5);
  pdfDoc.text("Tel: 347-730-1863", x, y + 10);
  pdfDoc.text("Email: info@sjtechnosolutions.com", x, y + 15);

  pdfDoc.text(reportTitle, x, 40);

  const accountString = `Account: ${store.storename.toUpperCase()}`;
  const addressString = `${store.street1}, ${store.city}, ${store.state}, ${store.country}, ${store.zipcode}`;
  pdfDoc.text(accountString, x, 50);
  pdfDoc.text(addressString, x, 55);

  const headers = columns.map((column) => column.title);

  pdfDoc.autoTable({
    head: [headers],
    body: data.map((product) =>
      columns.map((col) => {
        if (col.field === "unit_cost" || col.field === "ext_cost")
          return formatCurrency(product[col.field]);
        else return product[col.field];
      })
    ),
    startY: 60,
    styles: {
      margin: 0,
    },
  });

  const summaryStartY = pdfDoc.autoTable.previous.finalY + 15;

  pdfDoc.text("Summary", 14, summaryStartY);

  const summaryTable = [
    ...Object.entries(summaryData).map(([classname, data]) => [
      classname,
      formatCurrency(data.brand),
      formatCurrency(data.generics),
    ]),
    [
      "Total Value(Brand + Generics)",
      formatCurrency(
        Number(summaryData.Total.brand) + Number(summaryData.Total.generics)
      ),
      "",
    ],
  ];

  pdfDoc.autoTable({
    head: [["Class", "Brand", "Generics"]],
    body: summaryTable,
    startY: summaryStartY + 5,
    didParseCell: parseSummaryTableCell,
  });

  addPDFPageNumbers(pdfDoc);

  pdfDoc.save(reportTitle + ".pdf");
}

function parseSummaryTableCell(data) {
  const colIndex = data.column.index;
  const rowIndex = data.row.index;
  if (rowIndex > 3) {
    data.cell.styles.fontStyle = "bold";
  }

  if (colIndex === 2 && rowIndex === 6) {
    data.cell.colSpan = 2;
    data.cell.styles.halign = "center";
  }
}

function addPDFHeaders(pdfDoc) {
  let pageCount = pdfDoc.internal.pages.length;
  for (let i = 1; i < pageCount; i++) {
    pdfDoc.setPage(i);
    const x = 14;
    const y = 14;
    pdfDoc.text("85 Joan Dr", x, y);
    pdfDoc.text("Yonkers NY 10704", x, y + 12);
    pdfDoc.text("Tel: 347-730-1863", x, y + 24);
    pdfDoc.text("Email: info@sjtechnosolutions.com", x, y + 36);
    pageCount = pdfDoc.internal.pages.length;
  }
}

function addPDFPageNumbers(pdfDoc) {
  const pageCount = pdfDoc.internal.pages.length;

  for (let i = 1; i < pageCount; i++) {
    pdfDoc.setPage(i);
    const x = pdfDoc.internal.pageSize.width / 2;
    const y = pdfDoc.internal.pageSize.height - 10;
    pdfDoc.text(`${i} of ${pageCount - 1}`, x, y, { align: "center" });
  }
}
